// useRegistryGiftCounts.ts
import type { CalculatedPriceRangeFragment } from './RegistryGiftCounts.graphql.generated';
import { useGetAdminRegistryDataQuery } from '../../../admin/Registry.graphql.generated';
import { useGetRegistryRecommendationResultsQuery, useSetGuestCountMutation } from './RegistryGiftCounts.graphql.generated';
import { useTranslation } from '@shared/core';
import { getCurrencySymbolFromCurrencyCode } from '@shared/utils/currency/utils';
import type { FormikValues } from 'formik';
import { useFormik } from 'formik';
import { useCallback, useEffect, useMemo, useState } from 'react';
import * as Yup from 'yup';
import type { RegistryGiftCountsProps } from './RegistryGiftCounts';
import { useAdminRegistryTelemetry } from '@apps/registry/admin/AdminRegistry.telemetry';
import { useHistory } from '@react-router';
import type { Page } from './telemetry';
import { useRegistryGiftCountsTelemetry } from './telemetry';
import { qs } from '@shared/core/queryString';
import { useRoutePaths } from '@apps/registry/admin/Registry.routes';

export interface GuestCountInputFields {
  guestCount: number;
}

export const useRegistryGiftCounts = ({ eventId, page, eventHandle }: RegistryGiftCountsProps) => {
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [initializedGuestCount, setInitializedGuestCount] = useState<number | null>(null);
  const telemetry = useAdminRegistryTelemetry();

  const { t } = useTranslation('sharedComponents');
  const { totalCompletionCount, underPrice, overPrice, inputError, giftCountWithTarget } = t('registryGiftCounts');
  const { progressNotStarted, progressLessThanHalf, halfwayProgress, progressAlmostComplete, progressComplete, progressBeyondComplete } = t('giftProgress');

  const { data: initialData, loading: initialDataLoading } = useGetRegistryRecommendationResultsQuery({
    variables: { handle: eventHandle },
    batchMode: 'fast',
  });

  const { registryCheatSheetSaved } = useRegistryGiftCountsTelemetry();
  const results = initialData?.eventByName?.recommendation.registryAdvisor;

  // Initialize guest count when data is loaded
  useEffect(() => {
    if (!initialDataLoading && results?.guestCount && initializedGuestCount === null) {
      setInitializedGuestCount(results.guestCount);
    }
  }, [initialDataLoading, results?.guestCount, initializedGuestCount]);

  const [setGuestCount, { loading: setGuestCountLoading }] = useSetGuestCountMutation({
    refetchQueries: ['GetRegistryRecommendationResults'],
    awaitRefetchQueries: true,
  });

  const handleOnSave = async (values: FormikValues) => {
    const guestCount = +values.guestCount;
    setIsEditing(false);
    registryCheatSheetSaved(guestCount, page);
    await setGuestCount({ variables: { eventId, payload: { guestCount } } });
  };

  const formik = useFormik<GuestCountInputFields>({
    enableReinitialize: true,
    initialValues: {
      guestCount: initializedGuestCount ?? 100,
    },
    validationSchema: Yup.object<GuestCountInputFields>({
      guestCount: Yup.number().min(1, inputError()).integer(inputError()).required(inputError()).typeError(inputError()),
    }),
    onSubmit: values => handleOnSave(values),
  });

  const handleOnEditClick = () => {
    setIsEditing(!isEditing);
    telemetry.guestEditClick(page);
  };

  const targetGiftCount = results?.targetGiftCount || 0;
  const currentGiftCount = results?.currentGiftCount || 0;
  const giftsOverTarget = currentGiftCount > targetGiftCount;

  const completionPercent = results ? Math.round((results.currentGiftCount / results.targetGiftCount) * 100) : 0;
  const formattedCompletionPercent = completionPercent > 100 ? 100 : completionPercent;

  const controlledPercentage = useMemo(() => {
    if (completionPercent < 0) return 0;
    if (completionPercent > 100) return 100;
    return completionPercent;
  }, [completionPercent]);

  const subtitle = useMemo(() => {
    if (controlledPercentage === 0) return progressNotStarted();
    if (controlledPercentage > 0 && controlledPercentage < 50) return progressLessThanHalf();
    if (controlledPercentage > 49 && controlledPercentage < 60) return halfwayProgress();
    if (controlledPercentage > 59 && controlledPercentage < 99) return progressAlmostComplete();
    if (controlledPercentage === 100 && currentGiftCount === targetGiftCount) return progressComplete();
    return progressBeyondComplete();
  }, [
    controlledPercentage,
    progressNotStarted,
    progressLessThanHalf,
    halfwayProgress,
    progressAlmostComplete,
    currentGiftCount,
    targetGiftCount,
    progressComplete,
    progressBeyondComplete,
  ]);

  const getPriceRangeTitleAndLink = useCallback(
    (priceRange: CalculatedPriceRangeFragment) => {
      const startPrice = priceRange.startPrice.valueInMinorUnits === 0 ? null : priceRange.startPrice.valueInMinorUnits;
      const endPrice = !priceRange.endPrice ? null : priceRange.endPrice.valueInMinorUnits;
      const currencySymbol = getCurrencySymbolFromCurrencyCode(priceRange.startPrice.currency?.code);

      if (!startPrice && endPrice) {
        return {
          title: underPrice({ price: `${currencySymbol}${endPrice / 100}` }),
          priceRangeQuery: `-${endPrice}`,
          telemetryLabel: `Under${endPrice / 100}Count`,
          currentCount: priceRange.currentGiftCount,
        };
      } else if (!endPrice && startPrice) {
        return {
          title: overPrice({ price: `${currencySymbol}${startPrice / 100}` }),
          priceRangeQuery: `${startPrice}-`,
          telemetryLabel: `Over${startPrice / 100}Count`,
          currentCount: priceRange.currentGiftCount,
        };
      } else if (startPrice && endPrice) {
        return {
          title: `${currencySymbol}${startPrice / 100} - ${currencySymbol}${endPrice / 100}`,
          priceRangeQuery: `${startPrice}-${endPrice}`,
          telemetryLabel: `${startPrice / 100}to${endPrice / 100}Count`,
          currentCount: priceRange.currentGiftCount,
        };
      }
      return {
        title: '',
        priceRangeQuery: '',
        telemetryLabel: '',
        currentCount: priceRange.currentGiftCount,
      };
    },
    [overPrice, underPrice],
  );

  const completionCountTitle = useMemo(() => {
    if (completionPercent >= 100) {
      return totalCompletionCount({ giftCount: currentGiftCount });
    }
    return giftCountWithTarget({ giftCount: currentGiftCount, targetCount: targetGiftCount });
  }, [completionPercent, giftCountWithTarget, currentGiftCount, targetGiftCount, totalCompletionCount]);

  return {
    formik,
    getResultsLoading: setGuestCountLoading,
    initialDataLoading,
    isEditing,
    handleOnEditClick,
    completionCountTitle,
    formattedCompletionPercent,
    subtitle,
    getPriceRangeTitleAndLink,
    completionPercent,
    priceRanges: results?.priceRanges,
    formattedTotalCount: !results?.targetGiftCount ? null : `${currentGiftCount}/${targetGiftCount}`,
    giftsOverTarget,
    targetGiftCount,
    currentGiftCount,
    isInZeroState: !results?.targetGiftCount,
  };
};

export const useCashFundProgress = ({ eventHandle }: { eventHandle: string }) => {
  const { data } = useGetAdminRegistryDataQuery({
    batchMode: 'fast',
    variables: { eventHandle },
  });

  const donationFundInfo = data?.eventByName?.registry?.donationFundInfo;
  const cashFundTotalCount = donationFundInfo?.items?.length ?? 0;

  const cashFundCompletionPercent = useMemo(() => {
    return cashFundTotalCount > 0 ? 100 : 1;
  }, [cashFundTotalCount]);

  return {
    cashFundTotalCount,
    cashFundCompletionPercent,
  };
};

export const useGenerateInitialMessage = ({
  giftsCompletionPercent,
  cashFundCompletionPercent,
  guestCount,
  targetedGiftCount,
}: {
  giftsCompletionPercent: number;
  cashFundCompletionPercent: number;
  guestCount: number;
  targetedGiftCount: number;
}) => {
  const { t } = useTranslation('sharedComponents');
  const { initialMessageWhenZero, initialMessageWhenGreaterThanZero, initialMessageWhenGiftOnlyCompleted, initialMessageWhenCashFundOnlyCompleted } = t('guestPreferenceMessage');

  const giftGoalCompleted = giftsCompletionPercent >= 100;
  const cashFundGoalCompleted = cashFundCompletionPercent >= 100;

  if (giftsCompletionPercent === 0 && cashFundCompletionPercent === 0) {
    return initialMessageWhenZero({ targetedGiftCount, guestCount });
  }

  if (giftGoalCompleted && !cashFundGoalCompleted) {
    return initialMessageWhenGiftOnlyCompleted();
  }

  if (!giftGoalCompleted && cashFundGoalCompleted) {
    return initialMessageWhenCashFundOnlyCompleted({ giftsCompletionPercent, targetedGiftCount, guestCount });
  }

  if (giftsCompletionPercent > 0 && cashFundCompletionPercent > 0) {
    return initialMessageWhenGreaterThanZero({ giftsCompletionPercent, targetedGiftCount, guestCount });
  }
  return '';
};

export const useHandlePriceRangeClick = (page: Page, onActionComplete?: () => void) => {
  const history = useHistory();
  const { priceRangeClick } = useRegistryGiftCountsTelemetry();
  const paths = useRoutePaths();

  const handlePriceRangeClick = (title: string, priceRangeQuery: string) => {
    priceRangeClick(title, page, 'Gift Count / Price Point Guide');

    history.push({
      pathname: `${paths.catalog.path}/recommended`,
      search: qs.stringify({ pf: `priceRange=${priceRangeQuery}`, showFilters: true }),
    });
    onActionComplete?.();
  };

  return { handlePriceRangeClick };
};
